import { useQuery, useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import { Activity, Newspaper, BarChart3, Zap, RefreshCw } from 'lucide-react';
import { CandleChart, type PriceLineDef } from './components/CandleChart';
import { Watchlist } from './components/Watchlist';
import { MarketStatusStrip } from './components/MarketStatusStrip';
import { Mascot } from './components/Mascot';
import { DailyReportPanel } from './components/DailyReportPanel';
import { PanelCard } from './components/PanelCard';
import { FillExpand } from './components/FillExpand';
import { SignalSimpleCard } from './components/SignalSimpleCard';
import { CatalystPanel } from './components/CatalystPanel';
import { TodayActionPanel, DecisionDetailPanel, type ExecutionPlan } from './components/TodayActionPanel';
import { MyThesesPanel } from './components/MyThesesPanel';
import { StockHealthPanel } from './components/StockHealthPanel';
import { PositionEntryRow } from './components/PositionEntryRow';
import { TradePlanHitRatePanel } from './components/TradePlanHitRatePanel';
import { GeminiCopyButton } from './components/GeminiCopyButton';
import { MarketTabs } from './components/MarketTabs';
import { NotificationCenter } from './components/NotificationCenter';
import { CloudSyncBadge } from './components/CloudSyncBadge';
import { usePrefsSync } from './lib/prefs-sync';
import { useMarketCalendarSync } from './lib/useMarketCalendarSync';
import { Term, TERMS } from './components/Term';
import { useEnhancedQuote } from './lib/useEnhancedQuote';
import { enhanceSignalWithLivePrice } from './lib/enhanceSignal';
import { appendLiveBar, inferTimezone } from './lib/enhanceChart';
import { detectInitialMarket, defaultTimeframeIdx, symbolMarket, type Market } from './lib/market';
import { computeChartPriceLines } from './lib/chartLines';
import { computeSurge, surgeMagnitude, isVolBurst } from './lib/surge';
import { useMyPosition, usePortfolioSymbols } from './lib/usePortfolio';
import { fetchJSON as getJSON } from './lib/api-client';
import { isDelistedError, DELISTED_MESSAGE } from './lib/error-utils';
import { useNewsPrewarm } from './lib/useNewsPrewarm';
import { useSymbolName } from './lib/useSymbolName';
import { limitStatus } from './lib/limitStatus';
import { LimitTag } from './components/LimitTag';
import {
  DEFAULT_WATCHLIST,
  normalizeSymbolInput,
  type ChartResponse,
  type DayTradeScore,
  type IntradayRange,
  type Interval,
  type Institutional,
  type Margin,
  type NewsAnalysis,
  type NewsItem,
  type Quote,
  type Signal,
  type TechnicalSnapshot,
  type WatchlistItem,
} from '@invest-copilot/shared';

interface HealthResp { status: string; service: string; time: string; node: string; }
interface SignalResp { snapshot: TechnicalSnapshot; signal: Signal; dayTrade?: DayTradeScore; intradayRange?: IntradayRange; }
interface NewsResp { symbol: string; source: string; lang: string; region: string; items: NewsItem[]; warnings?: string[]; }
interface AnalyzeResp { items: NewsAnalysis[]; }

type NewsSource = 'auto' | 'google' | 'yahoo';

const REGION_PRESETS = [
  { label: '🇹🇼 台灣 zh-TW', lang: 'zh-TW', region: 'TW' },
  { label: '🇺🇸 美國 en-US', lang: 'en-US', region: 'US' },
  { label: '🇭🇰 香港 zh-HK', lang: 'zh-HK', region: 'HK' },
  { label: '🇯🇵 日本 ja', lang: 'ja', region: 'JP' },
  { label: '🇨🇳 中国 zh-CN', lang: 'zh-CN', region: 'CN' },
];

const TIMEFRAME_PRESETS = [
  { label: '1分', range: '1d', interval: '1m', intraday: true },
  { label: '5分', range: '1d', interval: '5m', intraday: true },
  { label: '30分', range: '5d', interval: '30m', intraday: true },
  { label: '60分', range: '5d', interval: '60m', intraday: true },
  { label: '日 1月', range: '1mo', interval: '1d', intraday: false },
  { label: '日 3月', range: '3mo', interval: '1d', intraday: false },
  { label: '日 6月', range: '6mo', interval: '1d', intraday: false },
  { label: '日 1年', range: '1y', interval: '1d', intraday: false },
];

const WATCHLIST_KEY = 'invest-copilot.watchlist.v2';
const ACTIVE_KEY = 'invest-copilot.active.v2';
const MARKET_KEY = 'invest-copilot.market.v1';
const ACTIVE_PER_MARKET_KEY = 'invest-copilot.active-per-market.v1';
const BUILD_TIME_LABEL = new Date(__APP_BUILD_TIME__).toLocaleString('zh-TW', { hour12: false });

function loadWatchlist(): WatchlistItem[] {
  try {
    const raw = localStorage.getItem(WATCHLIST_KEY);
    if (!raw) return DEFAULT_WATCHLIST;
    const parsed = JSON.parse(raw) as WatchlistItem[];
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_WATCHLIST;
    return parsed;
  } catch { return DEFAULT_WATCHLIST; }
}
function loadActive(fallback: string): string {
  try { return localStorage.getItem(ACTIVE_KEY) || fallback; } catch { return fallback; }
}
function loadStoredMarket(): Market | null {
  try { const v = localStorage.getItem(MARKET_KEY); return v === 'TW' || v === 'US' ? v : null; } catch { return null; }
}
function loadActivePerMarket(): Partial<Record<Market, string>> {
  try { const raw = localStorage.getItem(ACTIVE_PER_MARKET_KEY); return raw ? JSON.parse(raw) : {}; } catch { return {}; }
}

// ❄️ 企鵝雪屋下雪層：純裝飾、不影響資料流。配置在 module scope 算一次，render 不重算。
// 數量精簡到 9（原 14）降低持續合成負擔；背景分頁時由 .anim-paused 暫停。
const SNOWFLAKES = Array.from({ length: 9 }, (_, i) => ({
  left: (i * 11.3 + 4) % 100,
  size: 9 + (i % 4) * 3,
  duration: 11 + (i % 5) * 2.5,
  delay: (i % 7) * 1.6,
  glyph: i % 3 === 0 ? '❄' : i % 3 === 1 ? '❅' : '•',
}));

function Snow() {
  return (
    <div className="snow" aria-hidden="true">
      {SNOWFLAKES.map((f, i) => (
        <span
          key={i}
          className="flake"
          style={{
            left: `${f.left}%`,
            fontSize: `${f.size}px`,
            animationDuration: `${f.duration}s`,
            animationDelay: `${f.delay}s`,
          }}
        >
          {f.glyph}
        </span>
      ))}
    </div>
  );
}

// 🐧 分頁導覽列：錨點 + scroll-spy（捲動時 highlight 對應分頁）
const NAV_SECTIONS = [
  { id: 'sec-today', label: '今日行動', icon: '🎯' },
  { id: 'sec-research', label: '研究台', icon: '🔬' },
  { id: 'sec-plans', label: '交易計畫', icon: '📋' },
  { id: 'sec-news', label: '消息面', icon: '📰' },
  { id: 'sec-holdings', label: '每日報告', icon: '📝' },
  { id: 'sec-followup', label: '模擬跟單', icon: '📊' },
] as const;

function NavTabs() {
  const [activeId, setActiveId] = useState<string>(NAV_SECTIONS[0].id);

  useEffect(() => {
    const sections = NAV_SECTIONS.map((s) => document.getElementById(s.id)).filter(
      (el): el is HTMLElement => !!el,
    );
    if (sections.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-130px 0px -55% 0px', threshold: [0.01, 0.25, 0.5] },
    );
    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  function handleClick(e: ReactMouseEvent<HTMLAnchorElement>, id: string) {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
    }
  }

  return (
    <nav className="navtabs" aria-label="區塊導覽">
      {NAV_SECTIONS.map((s) => (
        <a
          key={s.id}
          href={`#${s.id}`}
          className={activeId === s.id ? 'on' : undefined}
          onClick={(e) => handleClick(e, s.id)}
        >
          <span>{s.icon}</span>
          {s.label}
        </a>
      ))}
    </nav>
  );
}

// 🐧 區塊標題：虛線分隔 + 企鵝裝飾
function SectionTitle({
  icon,
  children,
  count,
  deco = '🐧',
  deco2,
}: {
  icon: string;
  children: ReactNode;
  count?: string;
  deco?: string;
  deco2?: string;
}) {
  return (
    <div className="bar-title">
      <span className="ic">{icon}</span>
      {children}
      {count && <span className="cnt">{count}</span>}
      <span className="ln" />
      {deco && <span className="deco">{deco}</span>}
      {deco2 && <span className="deco d2">{deco2}</span>}
    </div>
  );
}

export function App() {
  const [watchlist, setWatchlist] = useState<WatchlistItem[]>(() => loadWatchlist());
  const [market, setMarket] = useState<Market>(() => detectInitialMarket(loadStoredMarket()));
  // 記住每個市場的 active symbol，切交 tab 變回來可以走上次那檔
  const [activePerMarket, setActivePerMarket] = useState<Partial<Record<Market, string>>>(() => loadActivePerMarket());
  const [active, setActive] = useState<string>(() => {
    const initialMarket = detectInitialMarket(loadStoredMarket());
    const wl = loadWatchlist();
    const stored = loadActive(wl.find((w) => w.market === initialMarket)?.symbol ?? wl[0]!.symbol);
    // 初始 active 不在初始 market 裡 → 換成該市場第一檔
    if (symbolMarket(stored) !== initialMarket) {
      return wl.find((w) => w.market === initialMarket)?.symbol ?? stored;
    }
    return stored;
  });
  const [newsSource, setNewsSource] = useState<NewsSource>('auto');
  const [regionIdx, setRegionIdx] = useState(0);
  // 進場機會卡「🔬 研究台看依據」帶入的 AI 決策計畫；只在其 symbol === active 時於研究台「AI 決策依據」卡顯示。
  const [researchPlan, setResearchPlan] = useState<ExecutionPlan | null>(null);
  const [tfIdx, setTfIdx] = useState(() => defaultTimeframeIdx(detectInitialMarket(loadStoredMarket()))); // 智慧預設：該市場盤中→ 5 分；休市→日線
  const region = REGION_PRESETS[regionIdx]!;
  const tf = TIMEFRAME_PRESETS[tfIdx]!;
  const activeIsTw = symbolMarket(active) === 'TW';
  const prefsSync = usePrefsSync();
  useMarketCalendarSync(); // 同步 TWSE 官方行事曆 override 到 shared（台股休市/倒數用）

  // 分頁切到背景時，在 <html> 掛 .anim-paused → CSS 暫停雪 / 浮動企鵝 / shimmer 等裝飾動畫，省 CPU。
  useEffect(() => {
    const apply = () => {
      document.documentElement.classList.toggle('anim-paused', document.hidden);
    };
    apply();
    document.addEventListener('visibilitychange', apply);
    return () => document.removeEventListener('visibilitychange', apply);
  }, []);

  useEffect(() => {
    try { localStorage.setItem(WATCHLIST_KEY, JSON.stringify(watchlist)); } catch {/**/}
  }, [watchlist]);
  useEffect(() => {
    try { localStorage.setItem(ACTIVE_KEY, active); } catch {/**/}
  }, [active]);
  useEffect(() => {
    try { localStorage.setItem(MARKET_KEY, market); } catch {/**/}
  }, [market]);
  useEffect(() => {
    try { localStorage.setItem(ACTIVE_PER_MARKET_KEY, JSON.stringify(activePerMarket)); } catch {/**/}
  }, [activePerMarket]);

  // 切換市場時：更新 active = 該市場上次看的那檔 (或該市場第一檔)、並重設 timeframe 為市場相關預設
  function switchMarket(m: Market) {
    if (m === market) return;
    setMarket(m);
    const next = activePerMarket[m] ?? watchlist.find((w) => w.market === m)?.symbol;
    if (next) setActive(next);
    setTfIdx(defaultTimeframeIdx(m));
  }

  // active 變動時記錄到 activePerMarket
  useEffect(() => {
    const m = symbolMarket(active);
    setActivePerMarket((prev) => prev[m] === active ? prev : { ...prev, [m]: active });
    // 若 user 手動選了另一個市場的股（不是 tab），同步 tab
    if (m !== market) setMarket(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const health = useQuery({ queryKey: ['health'], queryFn: () => getJSON<HealthResp>('/api/health') });
  const quote = useQuery({
    queryKey: ['quote', active],
    queryFn: () => getJSON<Quote>(`/api/quote/${encodeURIComponent(active)}`),
    enabled: !!active, refetchInterval: 5_000, // 即時報價：5 秒 refetch
    staleTime: 4_000,
    retry: 2, // 5xx 重試 2 次
    retryDelay: (i) => 300 * (i + 1),
  });
  // 台股自動加掘 TWSE 即時源（3 秒 polling，覆蓋 yahoo 的延遲數據）
  const enhancedQuote = useEnhancedQuote(active, quote.data);
  // 穩定公司名（官方 ISIN 上市櫃表 / offline map，cache 一天）：台股優先用它當大 K 線標題，
  // 避免 yahoo shortName 在 TWSE 撮合空檔退回英文（如「TA-I TECHNOLOGY」）造成中英跳動。
  const stableName = useSymbolName(active);
  // 依 interval 動態 refetch：1分/5秒、其他按比例
  const chartRefetchMs = (() => {
    if (!tf.intraday) return 5 * 60_000; // 日線：5 分鐘 refetch（為了跟到当日鬲巬）
    switch (tf.interval) {
      case '1m': return 5_000;
      case '5m': return 15_000;
      case '15m': return 30_000;
      case '30m': return 60_000;
      case '60m': return 60_000;
      default: return 30_000;
    }
  })();
  const chart = useQuery({
    queryKey: ['chart', active, tf.range, tf.interval],
    queryFn: () => getJSON<ChartResponse>(`/api/chart/${encodeURIComponent(active)}?range=${tf.range}&interval=${tf.interval}`),
    enabled: !!active, refetchInterval: chartRefetchMs,
    staleTime: Math.max(1_000, chartRefetchMs - 1_000),
    retry: 2,
    retryDelay: (i) => 400 * (i + 1),
  });
  const signal = useQuery({
    queryKey: ['signal', active],
    queryFn: () => getJSON<SignalResp>(`/api/signal/${encodeURIComponent(active)}`),
    enabled: !!active,
    refetchInterval: 5 * 60_000, // 盤中 5 分鐘重算一次
    staleTime: 4 * 60_000,
    retry: 2,
    retryDelay: (i) => 500 * (i + 1),
  });
  // 用即時價 overlay 到 signal（重算 %B / IntradayRange）
  const enhancedSignal = enhanceSignalWithLivePrice(signal.data, enhancedQuote);
  // K 線時區依 symbol
  const chartTimezone = inferTimezone(active);
  // 使用者在這檔股票的持倉（localStorage）
  const myPosition = useMyPosition(active);
  const portfolioSymbols = usePortfolioSymbols(market);
  // K 線上要標的水平線（開盤 / 昨收 / 預估區間 / 布林軌 / 我的成本）
  const chartPriceLines = useMemo(
    () =>
      computeChartPriceLines({
        price: enhancedQuote?.price,
        sessionOpenPrice: chart.data?.meta?.sessionOpenPrice ?? enhancedQuote?.open,
        prevClose: enhancedQuote?.prevClose,
        dayHigh: enhancedQuote?.dayHigh,
        dayLow: enhancedQuote?.dayLow,
        snapshot: enhancedSignal?.snapshot,
        intradayRange: enhancedSignal?.intradayRange,
        intraday: tf.intraday,
        // 單一交易日（1分/5分，range=1d）才畫漲跌停 / 今日高低 / 預估高低 / 今日開盤 / 昨收；
        // 多日盤中（30分/60分，range=5d 跨多天）這些「只對今天有意義」的線不畫。
        singleDay: tf.intraday && (tf.interval === '1m' || tf.interval === '5m'),
        myCostBasis: myPosition?.avgCost,
        candles: chart.data?.candles,
        symbol: active,
      }),
    [
      enhancedQuote?.price,
      enhancedQuote?.prevClose,
      enhancedQuote?.dayHigh,
      enhancedQuote?.dayLow,
      chart.data?.meta?.sessionOpenPrice,
      chart.data?.candles,
      enhancedSignal?.snapshot,
      enhancedSignal?.intradayRange,
      tf.intraday,
      tf.interval,
      myPosition?.avgCost,
    ]
  );
  const news = useQuery({
    queryKey: ['news', active, newsSource, region.lang, region.region],
    queryFn: () => getJSON<NewsResp>(`/api/news/${encodeURIComponent(active)}?count=12&source=${newsSource}&lang=${region.lang}&region=${region.region}`),
    enabled: !!active,
  });

  // === 新聞 AI 摘要批次預熱：自選 + 進場機會候選一次送 LLM 並寫 cache，點來點去不重複燒 ===
  // 進場機會候選與 IntradayOpportunityPanel 共用 queryKey，react-query 自動去重（不重複請求）。
  const intradayApiMarket = market === 'TW' ? 'tw' : 'us';
  const intradayOpp = useQuery({
    queryKey: ['intraday-opportunities', intradayApiMarket],
    queryFn: () =>
      getJSON<{ available: boolean; decisions?: { symbol: string }[] }>(
        `/api/intraday-opportunities?market=${intradayApiMarket}`,
      ),
    staleTime: 60 * 1000,
    retry: false,
  });
  const prewarmSymbols = useMemo(() => {
    const wl = watchlist.filter((w) => w.market === market).map((w) => w.symbol);
    const opp = intradayOpp.data?.available ? (intradayOpp.data.decisions ?? []).map((d) => d.symbol) : [];
    return Array.from(new Set([...wl, ...opp]));
  }, [watchlist, market, intradayOpp.data]);
  useNewsPrewarm({
    symbols: prewarmSymbols,
    source: newsSource,
    lang: region.lang,
    region: region.region,
    enabled: prewarmSymbols.length > 0,
  });

  // === 籌碼（台股專用）— 與 InstitutionalCard / MarginCard 共用 queryKey，react-query 自動去重 ===
  const institutionalQ = useQuery({
    queryKey: ['institutional', active],
    queryFn: () => getJSON<Institutional>(`/api/institutional/${encodeURIComponent(active)}`),
    enabled: !!active && activeIsTw,
    staleTime: 60 * 60_000,
    retry: false,
  });
  const marginQ = useQuery({
    queryKey: ['margin', active],
    queryFn: () => getJSON<Margin>(`/api/margin/${encodeURIComponent(active)}`),
    enabled: !!active && activeIsTw,
    staleTime: 60 * 60_000,
    retry: false,
  });
  // 處置 / 注意股狀態（台股專用）
  const dispositionQ = useQuery<{
    symbol: string;
    fetchedAt: string;
    punish?: { announcedAt: string; fromDate: string; toDate: string; condition: string; measure: string; cumulativeCount: string };
    notice?: { date: string; cumulativeCount: string; reason: string }[];
  }>({
    queryKey: ['disposition', active],
    queryFn: () => getJSON(`/api/disposition/${encodeURIComponent(active)}`),
    enabled: !!active && activeIsTw,
    staleTime: 60 * 60_000,
    retry: false,
  });

  function addToWatchlist(item: WatchlistItem) {
    const symbol = normalizeSymbolInput(item.symbol);
    if (!symbol) return;
    const normalizedItem: WatchlistItem = { ...item, symbol, market: symbolMarket(symbol) };
    setWatchlist((prev) => {
      if (prev.some((w) => w.symbol === symbol)) return prev;
      return [...prev, normalizedItem];
    });
    setActive(symbol);
  }

  // 樂觀加入後，背景解析回正確 .TW/.TWO + 公司名，把原樂觀項換掉（symbol 可能由 .TW 變 .TWO）。
  function reconcileWatchlistItem(placeholderSymbol: string, resolved: WatchlistItem) {
    const resolvedSymbol = normalizeSymbolInput(resolved.symbol);
    if (!resolvedSymbol) return;
    setWatchlist((prev) => {
      const idx = prev.findIndex((w) => w.symbol === placeholderSymbol);
      if (idx === -1) return prev;
      // 解析後與既有別檔撞號（重複加入）→ 移除這個 placeholder，避免兩個一樣的 chip。
      if (resolvedSymbol !== placeholderSymbol && prev.some((w) => w.symbol === resolvedSymbol)) {
        return prev.filter((_, i) => i !== idx);
      }
      const next = [...prev];
      next[idx] = { ...prev[idx]!, ...resolved, symbol: resolvedSymbol, market: symbolMarket(resolvedSymbol) };
      return next;
    });
    // placeholder 是當前 active 且 symbol 有變 → 同步 active，避免圖表卡在錯後綴。
    if (resolvedSymbol !== placeholderSymbol) {
      setActive((cur) => (cur === placeholderSymbol ? resolvedSymbol : cur));
    }
  }

  function removeFromWatchlist(symbol: string) {
    const next = watchlist.filter((w) => w.symbol !== symbol);
    setWatchlist(next);
    // 刪到的是當前 active → 只在「同市場」還有別檔時才改選，避免 active 跳到別市場連帶把 tab 切走。
    // 同市場已無別檔（例如把該市場自選清光）→ 保持 active / market 不動，不要硬跳到另一個市場。
    if (active === symbol) {
      const removedMarket = symbolMarket(symbol);
      const sameMarketNext = next.find((w) => w.market === removedMarket);
      if (sameMarketNext) setActive(sameMarketNext.symbol);
      // else: 同市場已空 → 不 setActive（保留在原市場，active 仍可看圖），不跳市場。
    }
  }

  function selectSymbol(symbol: string, addIfMissing = true) {
    const normalized = normalizeSymbolInput(symbol);
    if (!normalized) return;
    // addIfMissing=false：只切換 active 看圖（進場機會推薦卡用），不自動加入自選。
    if (addIfMissing && !watchlist.some((w) => w.symbol === normalized)) {
      addToWatchlist({ symbol: normalized, market: symbolMarket(normalized), name: normalized, category: '企鵝行動' });
      return;
    }
    setActive(normalized);
  }
  // 進場機會 / 我的部位卡點代號：切 active 看圖（不自動加自選）+ 捲到研究台，
  // 頂部結論細條已預設顯示該股 AI 依據、下方工作台 / 技術統計 / 個股體質同步更新。
  const viewSymbol = (symbol: string) => {
    selectSymbol(symbol, false);
    requestAnimationFrame(() => {
      document.getElementById('sec-research')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  // 研究台「AI 依據」：TodayActionPanel 依 active 股自動回報的 decision（researchPlan），預設對當前看的股票顯示。
  // researchActive 防 race：active 剛切、TodayActionPanel 還沒回報新 plan 時，舊 plan symbol 不符就先不顯示。
  const researchActive = researchPlan && researchPlan.decision.symbol === active ? researchPlan : null;

  return (
    <main className="min-h-screen pb-6 max-w-400 mx-auto">
      <Snow />
      {/* ====== Sticky 頂部 bar：永遠在第一屏 ====== */}
      <div className="sticky top-0 z-30 bg-(--bg-base)/95 backdrop-blur-md border-b border-(--border) px-4 pt-3 pb-2">
        {/* 第一行：logo + market tabs + 市場狀態鈕 + version + health */}
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-2xl animate-mascot shrink-0">🐧</span>
            <span className="text-xl animate-mascot shrink-0">🐧</span>
            <span className="text-2xl shrink-0">⛄</span>
            <h1 className="text-lg font-bold text-(--accent-deep) shrink-0">企鵝雷達</h1>
            <span className="text-[10px] text-(--text-muted) hidden md:inline truncate">
              📊 8 指標 · 🌍 新聞 · ✨ AI 訊號 · 🤖 Gemini
            </span>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <MarketTabs market={market} onChange={switchMarket} />
            <NotificationCenter />
            <CloudSyncBadge sync={prefsSync} />
            <BuildVersionBadge />
            <HealthBadge health={health.data} isError={health.isError} />
          </div>
        </div>

        {/* 第二行：watchlist chips（縮版，padding 壓低） */}
        <div className="-mx-1">
          <Watchlist
            items={watchlist.filter((w) => w.market === market)}
            active={active}
            onSelect={setActive}
            onAdd={addToWatchlist}
            onResolved={reconcileWatchlistItem}
            onRemove={removeFromWatchlist}
          />
        </div>

        {/* 第三行：分頁導覽列（錨點 + scroll-spy） */}
        <div className="mt-2">
          <NavTabs />
        </div>
      </div>

      {/* ====== 主內容區 ====== */}
      <div className="px-4 pt-3">
        {/* 市場狀態細條：單列 inline，不可摺疊，不佔版面 */}
        <MarketStatusStrip />

        <section id="sec-today" className="sec">
          <SectionTitle icon="🎯" deco="🐧">今日行動</SectionTitle>
          <TodayActionPanel
            market={market}
            watchlistSymbols={watchlist.filter((w) => w.market === market).map((w) => w.symbol)}
            portfolioSymbols={portfolioSymbols}
            onSelectSymbol={viewSymbol}
            activeSymbol={active}
            onActiveDecisionChange={setResearchPlan}
          />
        </section>

        <section id="sec-research" className="sec mb-3">
          <SectionTitle icon="🔬" count="工作台 · 訊號 · 體質" deco="🐧" deco2="❄️">
            研究台 · 依據層
          </SectionTitle>
          {researchActive && <DecisionDetailPanel plan={researchActive} />}
          <div className="work-grid">
          <PanelCard
            title="🎯 個股工作台"
            tag="放大 K 線 · 損益試算"
            actions={
              <GeminiCopyButton
                symbol={active}
                quote={enhancedQuote}
                signal={enhancedSignal?.signal}
                snapshot={enhancedSignal?.snapshot}
                intradayRange={enhancedSignal?.intradayRange}
                dayTrade={enhancedSignal?.dayTrade}
                institutional={institutionalQ.data}
                margin={marginQ.data}
                disposition={dispositionQ.data}
                news={news.data?.items}
              />
            }
          >
            <div className="space-y-3">
              <PositionEntryRow
                symbol={active}
                defaultPrice={enhancedQuote?.price}
                market={activeIsTw ? 'tw' : 'us'}
                isEtf={enhancedQuote?.quoteType === 'ETF'}
              />
              <ChartCard
                data={chart.data}
                loading={chart.isLoading}
                error={chart.error as Error | null}
                tfIdx={tfIdx}
                onTfChange={setTfIdx}
                intraday={tf.intraday}
                isFetching={chart.isFetching}
                timezone={chartTimezone}
                livePrice={enhancedQuote?.dataSource === 'twse' ? enhancedQuote.price : undefined}
                interval={tf.interval as Interval}
                symbol={active}
                shortName={activeIsTw ? (stableName ?? enhancedQuote?.shortName) : enhancedQuote?.shortName}
                priceLines={chartPriceLines}
                atr14={enhancedSignal?.intradayRange?.atr14}
                quotePrevClose={enhancedQuote?.prevClose}
              />
            </div>
          </PanelCard>

          <div className="flex flex-col gap-2.5 min-w-0 h-full">
            <PanelCard title="📊 技術統計依據" tag="多空因子 · 不是最終行動" className="flex-1" bodyClassName="flex flex-col min-h-0">
              <SignalSimpleCard
                bare
                symbol={active}
                snapshot={enhancedSignal?.snapshot}
                signal={enhancedSignal?.signal}
                liveOverlay={enhancedSignal?.liveOverlay}
                livePrice={enhancedSignal?.livePrice}
                loading={signal.isLoading}
                error={signal.error as Error | null}
                aiResearch={researchActive ? {
                  judgeReasoning: researchActive.decision.judgeReasoning,
                  bullPoints: researchActive.decision.bullPoints,
                  bearPoints: researchActive.decision.bearPoints,
                  conflicts: researchActive.decision.conflicts,
                } : null}
              />
            </PanelCard>

            <PanelCard title="🩺 個股體質" tag="風險 · 財報 · 基本面 · 籌碼">
              <StockHealthPanel symbol={active} isTw={activeIsTw} aiRisk={researchActive ? {
                failureConditions: researchActive.decision.failureConditions,
                riskWarnings: researchActive.decision.riskEvidence?.warnings,
              } : null} />
            </PanelCard>
          </div>
          </div>
        </section>

        <section id="sec-plans" className="sec mb-3">
          <SectionTitle icon="📋" deco="⛄">交易計畫</SectionTitle>
          <MyThesesPanel onSelectSymbol={selectSymbol} />
        </section>

        <section id="sec-news" className="sec mb-3">
          <SectionTitle icon="📰" count="盤後再看即可 · 非盤中即時" deco="📡">消息面 · 新聞 / 事件雷達</SectionTitle>
          <div className="news-event-pair">
            <PanelCard title="📰 新聞 AI 摘要" tag="情緒 · pivot 訊號" bodyClassName="flex flex-col min-h-0">
              <NewsCard
                bare
                symbol={active}
                data={news.data}
                loading={news.isLoading}
                error={news.error as Error | null}
                source={newsSource}
                onSourceChange={setNewsSource}
                regionIdx={regionIdx}
                onRegionChange={setRegionIdx}
              />
            </PanelCard>
            <PanelCard title="⚡ 事件雷達" tag="二階受惠 · 廣掃發現 · 關聯驗證" bodyClassName="flex flex-col min-h-0">
              <CatalystPanel onSelectSymbol={selectSymbol} embedded />
            </PanelCard>
          </div>
        </section>

        <section id="sec-holdings" className="sec mb-3 scroll-mt-33">
          <div id="daily-report-anchor">
            <PanelCard title="📝 每日盤後 AI 報告" tag="盤後總結 · 昨日回顧 · 明日重點">
              <DailyReportPanel onSelectSymbol={selectSymbol} />
            </PanelCard>
          </div>
        </section>

        <section id="sec-followup" className="sec mb-3 scroll-mt-33">
          <PanelCard title="📊 模擬跟單組合" tag="跟單回放 · 命中率 · 盤中免看">
            <TradePlanHitRatePanel onSelectSymbol={selectSymbol} embedded />
          </PanelCard>
        </section>

        <footer className="mt-6 text-[11px] text-(--text-muted) text-center pb-3">
          ⚡ Azure Functions · Gemini · Azure OpenAI · Yahoo + Google News · klinecharts ·
          <b className="text-(--text-secondary)"> 🐧 AI 輔助 · 扣板機自負</b>
        </footer>
      </div>

      <Mascot />
    </main>
  );
}

function HealthBadge({ health, isError }: { health?: HealthResp; isError: boolean }) {
  if (isError)
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs text-(--up) border border-(--up)/40 bg-(--up)/10">
        <span className="w-1.5 h-1.5 rounded-full bg-(--up)" /> API 離線
      </span>
    );
  if (!health)
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs text-(--text-muted) border border-(--border)">
        <span className="w-1.5 h-1.5 rounded-full bg-(--text-muted) pulse-dot" /> 連線中
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs text-(--down) border border-(--down)/40 bg-(--down)/10">
      <span className="w-1.5 h-1.5 rounded-full bg-(--down)" /> 連線中
    </span>
  );
}

function BuildVersionBadge() {
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs text-(--text-muted) border border-(--border) bg-(--bg-soft) tnum"
      title={`建置時間：${BUILD_TIME_LABEL} (${__APP_BUILD_TIME__})`}
    >
      <span className="text-(--accent)">版本</span>
      <span>v{__APP_VERSION__}</span>
    </span>
  );
}

function QuoteCard({ quote, loading, error }: { quote?: Quote; loading: boolean; error: Error | null }) {
  if (loading) return <div className="card"><Skeleton lines={3} /></div>;
  if (error) return <div className="card"><div className={isDelistedError(error) ? 'text-(--text-muted) text-sm' : 'text-(--up) text-sm'}>{isDelistedError(error) ? DELISTED_MESSAGE : `無法取得行情：${error.message}`}</div></div>;
  if (!quote) return null;

  const up = quote.change >= 0;
  const trendCls = up ? 'up' : 'down';
  const arrow = up ? '▲' : '▼';
  const isTW = quote.symbol.endsWith('.TW') || quote.symbol.endsWith('.TWO');

  return (
    <div className={`card ${up ? 'card-up' : 'card-down'}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {isTW ? (
              <>
                {/* 台股：公司名大、代號小 */}
                <div className="text-2xl font-bold text-(--text-primary) truncate max-w-[14ch]" title={quote.shortName}>
                  {quote.shortName ?? quote.symbol.replace(/\.(TW|TWO)$/i, '')}
                </div>
                <div className="text-xs text-(--text-muted) tnum">{quote.symbol}</div>
              </>
            ) : (
              <>
                {/* 美股：代號大、名稱小 */}
                <div className="text-2xl font-bold text-(--text-primary)">{quote.symbol}</div>
              </>
            )}
            <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded border ${
              quote.marketState === 'REGULAR' ? 'border-(--up)/40 text-(--up)'
              : quote.marketState === 'CLOSED' ? 'border-(--border-strong) text-(--text-muted)'
              : 'border-(--warn)/40 text-(--warn)'
            }`}>
              {quote.marketState}
            </span>
          </div>
          {!isTW && (
            <div className="text-(--text-muted) text-xs mt-0.5">{quote.shortName ?? '—'}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className={`${isTW ? 'text-2xl' : 'text-3xl'} font-bold tnum ${trendCls}`}>
            {quote.price.toFixed(2)}
          </div>
          <div className={`text-xs font-medium tnum ${trendCls} mt-0.5`}>
            {arrow} {Math.abs(quote.change).toFixed(2)} ({quote.changePct >= 0 ? '+' : ''}
            {quote.changePct.toFixed(2)}%)
          </div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3 pt-3 border-t border-(--border)">
        <Stat label="前收" value={quote.prevClose.toFixed(2)} />
        <Stat label="日高" value={quote.dayHigh?.toFixed(2) ?? '—'} />
        <Stat label="日低" value={quote.dayLow?.toFixed(2) ?? '—'} />
        <Stat label="量" value={quote.volume?.toLocaleString() ?? '—'} />
      </div>
      <div className="text-[10px] text-(--text-muted) mt-2.5 flex items-center gap-1.5 tnum flex-wrap">
        <Zap size={10} className="text-(--accent)" />
        <span>{new Date(quote.fetchedAt).toLocaleString('zh-TW')}</span>
        <span>·</span>
        <span>每 5s 自動刷新</span>
        <span>·</span>
        {quote.dataSource === 'twse' ? (
          <span title="TWSE 撮合系統，約 5-10 秒延遲" className="text-(--up) font-semibold border border-(--up)/40 rounded px-1">
            TWSE 即時
          </span>
        ) : (
          <span title="Yahoo Finance：美股盤中即時、台股約 15-20 分鐘延遲" className="text-(--text-muted) border border-(--border) rounded px-1">
            Yahoo{quote.symbol.endsWith('.TW') || quote.symbol.endsWith('.TWO') ? ' (~15min 延遲)' : ''}
          </span>
        )}
      </div>
    </div>
  );
}

function ChartCard({
  data, loading, error, tfIdx, onTfChange, intraday, isFetching, timezone, livePrice, interval, symbol, shortName, priceLines, headerExtra, atr14, quotePrevClose,
}: {
  data?: ChartResponse; loading: boolean; error: Error | null;
  tfIdx: number; onTfChange: (i: number) => void;
  intraday: boolean; isFetching: boolean;
  timezone?: string;
  livePrice?: number;
  interval: Interval;
  symbol: string;
  shortName?: string;
  priceLines?: PriceLineDef[];
  headerExtra?: ReactNode;
  atr14?: number;
  /** quote 的「真昨收」（盤中圖算當日漲跌% 用；meta.previousClose 在 5d/3mo 是 5天前/整段起點、不可信） */
  quotePrevClose?: number;
}) {
  // 計算 enhanced K 線（台股 TWSE 即時價 overlay 到最後一根）
  const displayData = data && livePrice != null
    ? appendLiveBar(data, livePrice, interval)
    : data;
  // 單日分時折線模式：只 1m/5m（range=1d 單一交易日）畫昨收基準上紅下綠折線；
  // 30m/60m（range=5d 多日）與日線用標準蜋燭。急漲急跌也只對單日折線有意義。
  const lineChart = intraday && (interval === '1m' || interval === '5m');
  const lastTime = displayData?.candles[displayData.candles.length - 1]?.timestamp;
  // 大 K 線即時急漲 / 急跌（只在盤中時間框；速度優先，與進場機會卡片同邏輯，見 lib/surge.ts）。
  const surge = useMemo(
    () => (lineChart ? computeSurge(displayData?.candles, displayData?.meta?.previousClose, symbol) : null),
    [lineChart, displayData, symbol],
  );
  const surgeMag = surge?.surge ? surgeMagnitude(surge) : 0;
  // 右上方現價 + 當日漲跌%：取最後一根 close vs「前一交易日收盤」。漲紅跌綠。
  // ⚠️ meta.previousClose 的語意隨 range 變：1d=昨收（對）、 5d（30/60分）=5天前、 3mo/1y（日線）=整段起點前收。
  // 後兩者拿來算「當日漲跌%」會差很多（實測 3231.TW 日線 158 vs 整段起點 129 = +22.48% 誤判漲停；
  // 實際今天 158 vs 昨天 165.5 = -4.53% 大跌）。故分流：
  //   - 日線（!intraday）：用倒數第二根 close（＝前一交易日收盤）。
  //   - 盤中（intraday，單日+多日）：用 quote 的真昨收（quotePrevClose），fallback meta.previousClose。
  const lastClose = displayData?.candles[displayData.candles.length - 1]?.close;
  const chartPrevClose = displayData?.meta?.previousClose;
  const candleCount = displayData?.candles.length ?? 0;
  const secondLastClose = candleCount >= 2 ? displayData?.candles[candleCount - 2]?.close : undefined;
  const refPrevClose = intraday ? (quotePrevClose ?? chartPrevClose) : (secondLastClose ?? chartPrevClose);
  const chgPct = lastClose != null && refPrevClose != null && refPrevClose > 0
    ? ((lastClose - refPrevClose) / refPrevClose) * 100
    : null;
  const chgCls = chgPct == null ? 'text-(--text-primary)'
    : chgPct > 0 ? 'text-(--up)' : chgPct < 0 ? 'text-(--down)' : 'text-(--text-muted)';
  const priceStr = lastClose != null
    ? lastClose.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '';
  // 台股漲跌停徽章：只在「單一交易日盤中圖」（1分/5分，lineChart）顯示——漲停是「今天整天 vs 昨收鎖死」的概念，
  // 日線（每根一天）與多日盤中（30/60分）顯示漲停徽章無意義（也避免被失真的 chgPct 誤觸發）。
  const lim = lineChart ? limitStatus(symbol, chgPct) : null;
  // 手動重置縮放：透過 bumping resetZoomKey 讓 CandleChart 以為 symbol 變了 → 重新 fitContent
  const [resetZoomKey, setResetZoomKey] = useState(0);
  const symbolForChart = `${symbol}#z${resetZoomKey}`;
  return (
    <div className="card">
      <div className="mb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <BarChart3 size={16} className="text-(--accent) shrink-0" />
            <div className="min-w-0">
              <div className="font-bold text-(--text-primary) text-sm flex items-center gap-1.5 flex-wrap">
                <span>{symbol}</span>
                {shortName && shortName !== symbol && (
                  <span className="text-(--text-secondary) font-semibold truncate max-w-60" title={shortName}>
                    {shortName}
                  </span>
                )}
                {livePrice != null && (
                  <span title="最後一根 K 棒以 TWSE 即時價更新" className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border border-(--up)/50 bg-(--up)/10 text-(--up)">⚡ 即時</span>
                )}
                {surge?.surge && (
                  <span
                    className={`chart-surge ${surge.surge}`}
                    title="近 3 根動能達門檻且最後一根同向（急漲/急跌）"
                  >
                    {surge.surge === 'up' ? '🔥 急漲' : '❄️ 急跌'} {surgeMag >= 0 ? '+' : ''}{surgeMag.toFixed(1)}%{isVolBurst(surge) ? ` ⚡量x${surge.volRatio.toFixed(1)}` : ''}
                  </span>
                )}
              </div>
              <div className="text-[11px] text-(--text-muted) tnum">
                {displayData?.meta?.exchangeName ?? '—'} ·{' '}
                {lastTime ? `最後 K：${new Date(lastTime * 1000).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' })}` : '—'}
                {isFetching && (
                  <span className="ml-2 inline-flex items-center gap-1 text-(--accent)">
                    <RefreshCw size={10} className="animate-spin" /> 更新中
                  </span>
                )}
              </div>
            </div>
          </div>
          {lastClose != null && (
            <div className="flex items-baseline gap-1.5 shrink-0 tnum leading-none" title="現價（最新 K 收盤）· 當日漲跌%">
              {lim ? (
                <>
                  <span
                    className="text-lg font-black px-1.5 rounded text-white"
                    style={{ background: lim === 'up' ? 'var(--up)' : 'var(--down)' }}
                  >
                    {priceStr}
                  </span>
                  <LimitTag status={lim} />
                </>
              ) : (
                <>
                  <span className={`text-lg font-bold ${chgCls}`}>{priceStr}</span>
                  {chgPct != null && (
                    <span className={`text-xs font-bold ${chgCls}`}>{chgPct >= 0 ? '▲' : '▼'} {chgPct >= 0 ? '+' : ''}{chgPct.toFixed(2)}%</span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className="mt-2 flex justify-end">
          <div className="seg">
            {TIMEFRAME_PRESETS.map((tf, i) => (
              <button
                key={tf.label}
                onClick={() => { onTfChange(i); setResetZoomKey((k) => k + 1); }}
                className={`seg-btn ${tfIdx === i ? 'active' : ''}`}
              >
                {tf.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setResetZoomKey((k) => k + 1)}
              title="重置縮放到適合的檢視範圍"
              className="seg-btn"
            >
              ↻
            </button>
          </div>
        </div>
      </div>
      {headerExtra}
      {loading && <Skeleton lines={6} />}
      {error && <div className={isDelistedError(error) ? 'text-(--text-muted) text-sm' : 'text-(--up) text-sm'}>{isDelistedError(error) ? DELISTED_MESSAGE : `無法取得 K 線：${error.message}`}</div>}
      {displayData && displayData.candles.length > 0 && <CandleChart candles={displayData.candles} intraday={intraday} lineChart={lineChart} timezone={timezone} priceLines={priceLines} symbol={symbolForChart} sessionOpenTs={data?.meta?.sessionOpen} prevClose={chartPrevClose} atr14={atr14} />}
      {displayData && displayData.candles.length === 0 && (
        <div className="text-(--text-muted) text-sm py-12 text-center">
          此區間沒有資料（可能是非交易時段或假日）
        </div>
      )}
    </div>
  );
}

function SignalCard({ data, loading, error }: {
  data?: SignalResp & { liveOverlay?: boolean; livePrice?: number };
  loading: boolean;
  error: Error | null;
}) {
  if (loading) return <div className="card"><Skeleton lines={5} /></div>;
  if (error) return <div className="card"><div className={isDelistedError(error) ? 'text-(--text-muted) text-sm' : 'text-(--up) text-sm'}>{isDelistedError(error) ? DELISTED_MESSAGE : `無法計算訊號：${error.message}`}</div></div>;
  if (!data) return null;

  const { snapshot, signal, liveOverlay, livePrice } = data;
  const px = livePrice ?? snapshot.price;
  // 離 MA 距離 %（以即時價 / fallback snapshot.price 為基準）
  const diffMa20 = snapshot.ma20 ? ((px - snapshot.ma20) / snapshot.ma20) * 100 : undefined;
  const diffMa60 = snapshot.ma60 ? ((px - snapshot.ma60) / snapshot.ma60) * 100 : undefined;
  const fmtDiff = (d?: number) => d == null ? '' : ` (${d >= 0 ? '+' : ''}${d.toFixed(2)}%)`;
  const regimeLabel: Record<string, string> = {
    trending: '📈 趨勢盤',
    ranging: '↔ 盤整',
    volatile: '⚡ 高波動',
  };
  const levelCls = (l: string) => {
    switch (l) {
      case 'STRONG_BUY': return 'signal-badge signal-strong-buy';
      case 'BUY': return 'signal-badge signal-buy';
      case 'SELL': return 'signal-badge signal-sell';
      case 'STRONG_SELL': return 'signal-badge signal-strong-sell';
      default: return 'signal-badge signal-hold';
    }
  };

  return (
    <div className="card card-accent">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity size={16} className="text-(--accent)" />
          <div>
            <div className="font-bold text-(--text-primary) text-sm flex items-center gap-1.5">
              技術綜合訊號
              {liveOverlay && (
                <span title="使用 TWSE 即時價重算 %B 、預估區間、離 MA 距離" className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded border border-(--up)/50 bg-(--up)/10 text-(--up)">
                  ⚡ 即時
                </span>
              )}
            </div>
            <div className="text-[11px] text-(--text-muted)">
              {snapshot.regime ? regimeLabel[snapshot.regime] : '—'}
              {snapshot.atrPct != null && ` · ATR ${(snapshot.atrPct * 100).toFixed(1)}%`}
              {liveOverlay && livePrice != null && ` · 現價 ${livePrice.toFixed(2)}`}
            </div>
          </div>
        </div>
        <span className={levelCls(signal.level)}>
          {signal.level.replace('_', ' ')} ({signal.score >= 0 ? '+' : ''}{signal.score})
        </span>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-3">
        <MiniStat
          label="MA20"
          value={snapshot.ma20 ? `${snapshot.ma20.toFixed(2)}${fmtDiff(diffMa20)}` : '—'}
          accent={diffMa20 == null ? 'flat' : diffMa20 > 0 ? 'up' : 'down'}
        />
        <MiniStat
          label="MA60"
          value={snapshot.ma60 ? `${snapshot.ma60.toFixed(2)}${fmtDiff(diffMa60)}` : '—'}
          accent={diffMa60 == null ? 'flat' : diffMa60 > 0 ? 'up' : 'down'}
        />
        <MiniStat
          label={<Term tip={TERMS.RSI}>RSI14</Term>}
          value={snapshot.rsi14?.toFixed(1) ?? '—'}
          accent={snapshot.rsi14 == null ? 'flat' : snapshot.rsi14 >= 70 ? 'up' : snapshot.rsi14 <= 30 ? 'down' : 'flat'}
        />
        <MiniStat
          label={<Term tip={TERMS.MACD}>MACD-H</Term>}
          value={snapshot.macd ? snapshot.macd.hist.toFixed(3) : '—'}
          accent={snapshot.macd ? (snapshot.macd.hist > 0 ? 'up' : 'down') : 'flat'}
        />
        <MiniStat label={<Term tip={TERMS.BB}>BB %B</Term>} value={snapshot.bollinger ? snapshot.bollinger.percentB.toFixed(2) : '—'} />
        <MiniStat
          label={<Term tip={TERMS.Stoch}>Stoch K</Term>}
          value={snapshot.stochastic ? snapshot.stochastic.k.toFixed(1) : '—'}
          accent={snapshot.stochastic == null ? 'flat' : snapshot.stochastic.k >= 80 ? 'up' : snapshot.stochastic.k <= 20 ? 'down' : 'flat'}
        />
        <MiniStat label={<Term tip={TERMS.VolRatio}>量比</Term>} value={snapshot.volRatio?.toFixed(2) ?? '—'} />
        <MiniStat
          label={<Term tip={TERMS.OBV}>OBV 背離</Term>}
          value={
            snapshot.obvDivergence === 'bullish' ? '多' :
            snapshot.obvDivergence === 'bearish' ? '空' : '—'
          }
          accent={
            snapshot.obvDivergence === 'bullish' ? 'up' :
            snapshot.obvDivergence === 'bearish' ? 'down' : 'flat'
          }
        />
      </div>

      <ul className="space-y-1.5">
        {signal.factors.map((f) => (
          <li key={f.name} className="flex items-start gap-2 text-[11px]">
            <span className={`shrink-0 mt-0.5 px-1 py-0.5 rounded text-[9px] font-bold tnum w-8 text-center ${
              f.score > 0 ? 'bg-(--up)/20 text-(--up)'
              : f.score < 0 ? 'bg-(--down)/20 text-(--down)'
              : 'bg-(--text-muted)/20 text-(--text-muted)'
            }`}>
              {f.score > 0 ? `+${f.score}` : f.score}
            </span>
            <div>
              <span className="text-(--text-primary) font-medium">{f.name}</span>
              <span className="text-(--text-muted) ml-1">— {f.reason}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NewsCard({
  symbol, data, loading, error, source, onSourceChange, regionIdx, onRegionChange, bare = false,
}: {
  symbol: string; data?: NewsResp; loading: boolean; error: Error | null;
  source: NewsSource; onSourceChange: (s: NewsSource) => void;
  regionIdx: number; onRegionChange: (i: number) => void;
  bare?: boolean;
}) {
  const analyze = useMutation({
    mutationFn: async (items: NewsItem[]) =>
      getJSON<AnalyzeResp>('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbol, news: items }),
      }),
  });

  // 自動觸發 analyze：新聞 data 一進來就跑（不要再讓使用者按按鈕）
  // 用 newsKey 當 dependency（每組新聞 fingerprint），避免同樣資料重複打
  const newsKey = data?.items?.map((n) => n.id).join('|') ?? '';
  useEffect(() => {
    if (!data?.items?.length) return;
    if (analyze.isPending) return;
    if (analyze.data && analyze.variables && JSON.stringify(analyze.variables.map((i) => i.id)) === JSON.stringify(data.items.map((i) => i.id))) return;
    analyze.mutate(data.items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsKey]);

  const [showSettings, setShowSettings] = useState(false);

  const analysisById = new Map<string, NewsAnalysis>();
  if (analyze.data) for (const a of analyze.data.items) analysisById.set(a.newsId, a);

  // 聚合摘要：跨所有新聞的方向 / pivot count / sentiment 統計
  const summary = (() => {
    if (!analyze.data?.items?.length) return null;
    const items = analyze.data.items;
    let pos = 0, neu = 0, neg = 0;
    let pivots: NewsAnalysis[] = [];
    let highSeverity: NewsAnalysis[] = [];
    for (const a of items) {
      if (a.sentiment === 'positive') pos++;
      else if (a.sentiment === 'negative') neg++;
      else neu++;
      if (a.aiPivotSignal) pivots.push(a);
      if (a.severity === 'high') highSeverity.push(a);
    }
    const total = items.length;
    const dominant: 'positive' | 'negative' | 'neutral' =
      pos > neg && pos >= neu ? 'positive' :
      neg > pos && neg >= neu ? 'negative' :
      'neutral';
    // 找最有代表性的 thesis（pivot > high severity > 首則）
    const topAnalysis = pivots[0] ?? highSeverity[0] ?? items[0];
    return { total, pos, neu, neg, pivots, dominant, topAnalysis };
  })();

  const dominantCls =
    summary?.dominant === 'positive' ? 'text-(--up) border-(--up)/40 bg-(--up)/8' :
    summary?.dominant === 'negative' ? 'text-(--down) border-(--down)/40 bg-(--down)/8' :
    'text-(--text-secondary) border-(--border) bg-black/20';
  const dominantLabel =
    summary?.dominant === 'positive' ? '整體偏多' :
    summary?.dominant === 'negative' ? '整體偏空' : '整體中性';

  return (
    <div className={bare ? 'flex flex-col min-h-0 flex-1' : 'card'}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div className="flex items-center gap-2 min-w-0">
          {!bare && <Newspaper size={16} className="text-(--accent) shrink-0" />}
          <div className="min-w-0">
            <div className="font-bold text-(--text-primary) text-sm flex items-center gap-1.5 flex-wrap">
              {!bare && <span>新聞 AI 摘要</span>}
              {analyze.isPending && (
                <span className="inline-flex items-center gap-1 text-[10px] text-(--accent)">
                  <RefreshCw size={10} className="animate-spin" /> 分析中
                </span>
              )}
              {summary && (
                <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded border ${dominantCls}`}>
                  {dominantLabel}
                </span>
              )}
              {summary && summary.pivots.length > 0 && (
                <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-(--warn) text-[#0f1115]" title="跨界 AI 訊號：可能影響其他標的的關聯事件">
                  ⭐ {summary.pivots.length} pivot
                </span>
              )}
            </div>
            <div className="text-[11px] text-(--text-muted) tnum">
              {source === 'auto' ? 'Google + Yahoo' : source} · {data?.lang ?? '—'}/{data?.region ?? '—'} · {data?.items.length ?? 0} 則
              {summary && ` · 多 ${summary.pos} 空 ${summary.neg} 中 ${summary.neu}`}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowSettings(!showSettings)}
            className="h-7 inline-flex items-center gap-1 rounded border border-(--border) px-2 text-[11px] text-(--text-muted) hover:border-(--accent) hover:text-(--accent)"
            title="新聞來源 / 語言設定"
          >
            ⚙ 設定
          </button>
        </div>
      </div>

      {/* === 設定 popover === */}
      {showSettings && (
        <div className="mb-3 p-2.5 rounded border border-(--border) bg-black/20 flex items-center gap-2 flex-wrap">
          <div className="text-[11px] text-(--text-muted)">來源</div>
          <div className="seg">
            {(['auto', 'google', 'yahoo'] as NewsSource[]).map((v) => (
              <button
                key={v}
                onClick={() => onSourceChange(v)}
                className={`seg-btn ${source === v ? 'active' : ''}`}
              >
                {v === 'auto' ? 'Auto' : v === 'google' ? 'Google' : 'Yahoo'}
              </button>
            ))}
          </div>
          <div className="text-[11px] text-(--text-muted) ml-2">區域</div>
          <select
            title="新聞語言與地區"
            aria-label="新聞語言與地區"
            value={regionIdx}
            onChange={(e) => onRegionChange(Number(e.target.value))}
            className="btn btn-ghost text-xs px-2"
          >
            {REGION_PRESETS.map((r, i) => (
              <option key={r.label} value={i} className="bg-(--bg-card)">{r.label}</option>
            ))}
          </select>
        </div>
      )}

      {loading && <Skeleton lines={2} />}
      {error && <div className={isDelistedError(error) ? 'text-(--text-muted) text-sm' : 'text-(--up) text-sm'}>{isDelistedError(error) ? DELISTED_MESSAGE : `無法取得新聞：${error.message}`}</div>}
      {analyze.error && (() => {
        const msg = (analyze.error as Error).message || '';
        const lower = msg.toLowerCase();
        const isAiDown = msg.includes('503') || msg.includes('429') ||
          lower.includes('quota') || lower.includes('resource_exhausted') || lower.includes('ai_unavailable');
        if (isAiDown) {
          return (
            <div className="mb-2 p-2 rounded border border-(--warn)/40 bg-(--warn)/10 text-[12px] text-(--text-secondary)">
              <span className="text-(--warn) font-bold">⚠️ AI 服務暫時忙碌</span>
              <span className="ml-1">新聞語意分析稍後再試（新聞列表正常顯示）。</span>
            </div>
          );
        }
        return <div className="text-(--up) text-sm mb-2">AI 分析失敗：{msg}</div>;
      })()}
      {data?.warnings && data.warnings.length > 0 && (
        <div className="text-[11px] text-(--warn) mb-2">{data.warnings.map((w) => <div key={w}>⚠ {w}</div>)}</div>
      )}

      {/* === AI 摘要主體（預設展開）=== */}
      {summary?.topAnalysis && (
        <div className="rounded border border-(--accent)/30 bg-(--accent)/4 p-3 mb-3">
          <div className="text-[12px] text-(--text-primary) leading-relaxed">{summary.topAnalysis.thesis}</div>
          <div className="mt-2 grid sm:grid-cols-2 gap-2">
            {summary.topAnalysis.bullishPoints.length > 0 && <BulletList title="📈 多方" cls="up" items={summary.topAnalysis.bullishPoints} />}
            {summary.topAnalysis.bearishPoints.length > 0 && <BulletList title="📉 空方" cls="down" items={summary.topAnalysis.bearishPoints} />}
          </div>
          {summary.pivots.length > 0 && (
            <div className="mt-2 pt-2 border-t border-(--warn)/30 space-y-1">
              <div className="text-[11px] font-semibold text-(--warn)">⭐ 跨界 AI 訊號</div>
              {summary.pivots.slice(0, 3).map((p) => {
                const n = data?.items.find((x) => x.id === p.newsId);
                return (
                  <div key={p.newsId} className="text-[11px] text-(--text-secondary) leading-relaxed">
                    {n && (
                      <a href={n.url} target="_blank" rel="noopener noreferrer" className="text-(--warn) hover:underline">
                        {n.title}
                      </a>
                    )}
                    {p.pivotNote && <span className="text-(--text-muted) ml-1.5">— {p.pivotNote}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {!summary && !analyze.isPending && data?.items && data.items.length > 0 && (
        <div className="rounded border border-(--border) bg-black/20 p-3 mb-3 text-[12px] text-(--text-muted)">
          AI 分析尚未完成或無法生成摘要 — 可展開原始新聞自行判讀。
        </div>
      )}

      {/* === 原始新聞：FillExpand 填補等高留白，超出才「展開全部」 === */}
      {data?.items && data.items.length > 0 && (
        <FillExpand moreLabel={`展開原始新聞（${data.items.length} 則）`} lessLabel="收合新聞">
          <div className="border-t border-(--border) pt-2">
            <div className="text-[11px] text-(--text-muted) mb-2">📰 原始新聞（{data.items.length} 則）</div>
            <ul className="space-y-2">
          {data?.items.map((n) => {
            const a = analysisById.get(n.id);
            const isPivot = a?.aiPivotSignal === true;
            return (
              <li
                key={n.id}
                className={`p-2.5 border relative overflow-hidden rounded ${
                  isPivot
                    ? 'border-(--warn)/60 bg-(--warn)/5'
                    : 'border-(--border) bg-(--bg-base)/30 hover:border-(--border-strong)'
                }`}
              >
                {isPivot && <div className="absolute top-0 left-0 right-0 h-0.5 pivot-shimmer" />}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <a
                      href={n.url} target="_blank" rel="noopener noreferrer"
                      className="text-(--text-primary) hover:text-(--accent) font-medium text-sm leading-snug"
                    >
                      {n.title}
                    </a>
                  </div>
                  {a && <SentimentBadge sentiment={a.sentiment} severity={a.severity} />}
                </div>
                <div className="text-[10px] text-(--text-muted) mt-1 tnum">
                  {n.publisher ?? '—'} · {new Date(n.publishedAt).toLocaleString('zh-TW')}
                </div>
                {a && (a.bullishPoints.length > 0 || a.bearishPoints.length > 0) && (
                  <div className="mt-1.5 pl-2 border-l-2 border-(--accent)/40 grid sm:grid-cols-2 gap-2">
                    {a.bullishPoints.length > 0 && <BulletList title="📈 多" cls="up" items={a.bullishPoints} />}
                    {a.bearishPoints.length > 0 && <BulletList title="📉 空" cls="down" items={a.bearishPoints} />}
                  </div>
                )}
              </li>
            );
          })}
            </ul>
          </div>
        </FillExpand>
      )}
    </div>
  );
}

function SentimentBadge({ sentiment, severity }: { sentiment: NewsAnalysis['sentiment']; severity: NewsAnalysis['severity'] }) {
  const cls: Record<NewsAnalysis['sentiment'], string> = {
    positive: 'border-(--up)/40 text-(--up) bg-(--up)/10',
    neutral: 'border-(--border-strong) text-(--text-muted) bg-(--text-muted)/10',
    negative: 'border-(--down)/40 text-(--down) bg-(--down)/10',
  };
  return (
    <span className={`shrink-0 text-[10px] rounded px-1.5 py-0.5 border whitespace-nowrap uppercase tracking-wider font-semibold ${cls[sentiment]}`}>
      {sentiment} · {severity}
    </span>
  );
}

function BulletList({ title, cls, items }: { title: string; cls: string; items: string[] }) {
  return (
    <div>
      <div className={`text-[11px] font-semibold mb-1 ${cls}`}>{title}</div>
      <ul className="text-[11px] text-(--text-secondary) space-y-0.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-1">
            <span className="text-(--text-muted) mt-0.5">·</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-(--text-muted) text-[10px] uppercase tracking-wider">{label}</div>
      <div className="text-(--text-primary) font-semibold tnum text-sm mt-0.5">{value}</div>
    </div>
  );
}

function MiniStat({ label, value, accent = 'flat' }: { label: ReactNode; value: string; accent?: 'up' | 'down' | 'flat' }) {
  return (
    <div className="rounded bg-(--bg-base) border border-(--border) px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-(--text-muted)">{label}</div>
      <div className={`text-sm font-semibold tnum mt-0.5 ${accent}`}>{value}</div>
    </div>
  );
}

function Skeleton({ lines = 3 }: { lines?: number }) {
  const widths = ['w-3/4', 'w-2/3', 'w-5/6', 'w-1/2', 'w-4/5', 'w-3/5', 'w-11/12'];
  return (
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className={`h-3 skeleton ${widths[i % widths.length]}`} />
      ))}
    </div>
  );
}
